import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { CondicionesCampana, LlaveService } from 'src/app/shared/services/llave.service';
import { MarketingOrigenService } from 'src/app/shared/services/marketing-origen.service';
import { SignupStateService } from './services/signup-state.service';
import { SignupPreviewService } from './services/signup-preview.service';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { Subscription } from 'rxjs';
import { SignupState } from './interfaces/signup.interfaces';
import { AppState } from 'src/app/app.reducer';
import { setUser } from 'src/app/auth/auth.actions';
import { User } from 'src/app/shared/models/user.model';
import {
  PREVIEW_EMAIL,
  PREVIEW_ERUT,
  PREVIEW_RUT_REPRESENTANTE_LIMPIO,
  PREVIEW_USUARIO_ID,
} from './constants/preview-empresa.fixtures';
import {
  PASOS_COMUNES,
  PASOS_EMPRESA,
  PASOS_PERSONA,
} from './constants/signup.constants';
import Swal from 'sweetalert2';
import { PuedeAbandonarRegistro } from 'src/app/shared/guards/signup-abandono.guard';

@Component({
  selector: 'app-dinamic-signup',
  templateUrl: './dinamic-signup.component.html',
  styleUrls: ['./dinamic-signup.component.sass'],
  providers: [SignupStateService], // Servicio a nivel de componente
})
export class DinamicSignupComponent
  implements OnInit, OnDestroy, PuedeAbandonarRegistro
{
  state!: SignupState;

  // Expuestos para el template: los `*ngIf` de cada paso se comparan contra
  // estos mapas y no contra numeros sueltos.
  readonly pasosEmpresa = PASOS_EMPRESA;
  readonly pasosPersona = PASOS_PERSONA;

  loadingFull = false;
  errorMessage = '';
  successMessage = '';
  isTransitioning = false;
  showContent = false;

  private stateSubscription!: Subscription;
  private transitionSubscription!: Subscription;
  private errorTimeout: any;
  private successTimeout: any;

  constructor(
public signupState: SignupStateService,
    public preview: SignupPreviewService,
    private cognitoService: CognitoService,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private llaveService: LlaveService,
    private marketingOrigen: MarketingOrigenService
  ) {}

  ngOnInit(): void {
    // Sin await: el wizard arranca igual. Ninguna de las dos llamadas de
    // adentro lanza —las dos devuelven un valor neutro ante un error de red—
    // asi que no queda una promesa rechazada suelta.
    void this.capturarLlaveDelLink();

    // El link de campana de Marketing (`?origen=`). No lanza ni se espera.
    this.marketingOrigen.capturar(this.route.snapshot.queryParamMap.get('origen'));

    // Suscribirse al estado
    this.stateSubscription = this.signupState.state$.subscribe((state) => {
      this.state = state;
    });

    // Suscribirse a las transiciones
    this.transitionSubscription = this.signupState.isTransitioning$.subscribe(
      (transitioning) => {
        this.isTransitioning = transitioning;
      }
    );

    // Preview del registro de empresa: el estado lo siembra el servicio, no hay
    // nada persistido que restaurar ni sesion que validar.
    if (this.preview.activo) {
      this.arrancarPreview();
      return;
    }

    // Verificar estado persistido
    this.checkPersistedState();
  }

  // ==================== PREVIEW ====================

  /**
   * Arranca el recorrido de prueba: siembra el estado y deja un usuario falso en
   * el store.
   *
   * El usuario hace falta porque los ultimos pasos leen `usuarioId` de `auth` y
   * cortan con "No pudimos identificar la empresa" si no lo encuentran. No toca
   * Cognito: es solo el objeto en memoria de ngrx.
   */
  private arrancarPreview(): void {
    this.preview.sembrar(this.signupState);
    this.store.dispatch(setUser({ user: this.usuarioDePreview() }));
    this.showContent = true;
  }

  /** Todos los pasos, para los botones de salto de la barra de preview. */
  get pasosParaSaltar(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i + 1);
  }

  /**
   * Salta a un paso sin recorrer los anteriores. Vuelve a sembrar para que el
   * paso destino encuentre sus prerrequisitos: el del representante legal sin
   * `ocrData` rebota al del OCR.
   */
  async saltarAPaso(paso: number): Promise<void> {
    if (!this.preview.activo) return;
    this.preview.sembrarHasta(paso, this.signupState);
  }

  private usuarioDePreview(): User {
    return {
      usuarioId: PREVIEW_USUARIO_ID,
      email: PREVIEW_EMAIL,
      nombre: 'MARIA JOSE',
      apellidos: 'SOTO RIVERA',
      tipoId: 'RUT',
      numeroId: PREVIEW_RUT_REPRESENTANTE_LIMPIO,
      prefijoTelefono: '+56',
      telefono: '912345678',
      sexo: 'F',
      fechaNacimiento: '1988-03-14',
      direccion: PREVIEW_ERUT.fields.direccion || '',
      profesion: '',
      paisResidencia: 'Chile',
      ciudadResidencia: 'Santiago',
      nacionalidad: 'CHILENA',
      politicamenteExpuesto: false,
      cognitoId: 'preview-cognito-id',
      razonSocial: PREVIEW_ERUT.fields.razonSocial || '',
      rutEmpresa: PREVIEW_ERUT.fields.rut || '',
    };
  }

  // ==================== ABANDONO ====================

  /**
   * ¿Hay trabajo que se perdería al salir?
   *
   * Solo mientras el documento y la selfie viven en memoria: desde que arranca
   * la captura del documento hasta que la verificación facial las sube a S3.
   * Antes del OCR no hay nada que perder, y del e-RUT en adelante ya está todo
   * en el servidor, así que avisar sería ruido — y el aviso que aparece siempre
   * es el aviso que la gente aprende a ignorar.
   */
  tieneTrabajoSinGuardar(): boolean {
    if (this.preview.activo) {
      return false;
    }
    if (!this.signupState.isEmpresa || !this.signupState.empresaFlujoNuevo) {
      return false;
    }
    const paso = this.signupState.currentStep;
    return paso >= PASOS_EMPRESA.OCR && paso <= PASOS_EMPRESA.SELFIE;
  }

  /**
   * Avisa al cerrar o refrescar la pestaña.
   *
   * El navegador muestra su propio texto y no se puede personalizar: este hook
   * solo frena la salida. El "qué se pierde" va en pantalla (ver el aviso del
   * template) y en el diálogo del guard, que sí es nuestro.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.tieneTrabajoSinGuardar()) {
      return;
    }
    event.preventDefault();
    // Chrome exige returnValue; el string es ignorado desde 2016.
    event.returnValue = '';
  }

  // Acá NO va un `pagehide`, y es a propósito.
  //
  // El plan lo pedía por Safari en iOS, donde `beforeunload` no dispara de
  // forma confiable. Pero `pagehide` no puede mostrar un diálogo: es el evento
  // de "ya te fuiste", no de "estás por irte". El registro viejo lo usa para
  // hacer cleanup síncrono, que es otro propósito.
  //
  // Lo que protege al usuario de iOS es que el estado se persiste en cada
  // cambio (`persistState`), así que al volver retoma donde estaba. Avisarle
  // no se puede; perderlo, tampoco.

  /**
   * Lee la llave de quien invito y la guarda en el estado del wizard.
   *
   * De aca en adelante viaja sola: `SignupStateService` persiste el estado en
   * sessionStorage y localStorage, asi que sobrevive los quince pasos, un
   * cambio de pestaña y hasta cerrar el navegador. No hay que tocar ningun
   * paso intermedio.
   *
   * Se guarda aunque todavia no se sepa si la llave existe. La validacion es
   * solo para poder mostrar "te invito @X"; que falle no impide nada, y el
   * backend vuelve a resolverla cuando se manda la intencion.
   */
  /**
   * Las condiciones de la campaña de quien invita, tal como las manda el API
   * al validar la llave. `null` si no tiene campaña o no se pudieron leer.
   */
  private condicionesReferidor: CondicionesCampana | null = null;

  /**
   * Lo que gana el invitado y qué tiene que hacer, o vacío.
   *
   * **Sólo después de elegir el documento**, y sólo si la campaña de quien
   * invita es de ese país (o para todos): hasta saber de dónde es el invitado
   * no se le promete nada. Una empresa registra RUT, así que cuenta como
   * Chile. Ni el monto ni el mínimo se escriben a mano: salen de la campaña.
   */
  get fraseInvitacion(): string {
    const documento =
      this.state?.accountType === 'empresas' ? 'chile' : this.state?.selectedDocumentType;
    return this.llaveService.fraseRegistro(
      this.llaveService.condicionesParaDocumento(this.condicionesReferidor, documento)
    );
  }

  private async capturarLlaveDelLink(): Promise<void> {
    const cruda = this.route.snapshot.queryParamMap.get('llave');

    // Sin llave en la URL puede venir una ya guardada en el wizard (volvió
    // después, o recargó sin el `?llave=`): se vuelven a leer sus condiciones
    // para que la barra siga diciendo lo mismo. No se guarda nada nuevo.
    if (!cruda) {
      const guardada = this.signupState.currentState.llaveReferidor;
      if (guardada) {
        const r = await this.llaveService.buscarReferidor(guardada);
        if (r.existe) this.condicionesReferidor = r.condiciones ?? null;
      }
      return;
    }

    const llave = this.llaveService.normalizar(cruda);
    if (!llave) return;

    // Programa apagado: el link no se captura.
    //
    // El backend descarta la intencion mientras esta apagado, asi que mostrar
    // "te invito @X" durante quince pasos seria anunciar un vinculo que no se
    // va a guardar. El invitado se registra como cualquier otro cliente.
    const programa = await this.llaveService.programa();
    if (!programa?.activo) return;

    const r = await this.llaveService.buscarReferidor(llave);

    // Un link mal escrito no se guarda: sin esto el wizard arrastraria una
    // llave inexistente hasta el paso 3 para que el backend la rechace
    // ahi, y el invitado nunca sabria que su link no servia.
    if (!r.existe || !r.llave) return;

    this.signupState.setLlaveReferidor(r.llave);

    this.condicionesReferidor = r.condiciones ?? null;
  }

  ngOnDestroy(): void {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }
    if (this.transitionSubscription) {
      this.transitionSubscription.unsubscribe();
    }
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
    }
  }

  // ==================== PERSISTENCIA ====================

  private async checkPersistedState(): Promise<void> {
    const state = this.signupState.currentState;

    // Sin progreso → no hay nada que restaurar
    if (state.currentStep <= PASOS_COMUNES.TIPO_CUENTA || !state.accountType) {
      this.showContent = true;
      return;
    }

    // Progreso sin email verificado → no tiene sentido continuar, reset
    if (!state.emailVerified) {
      this.signupState.reset();
      this.showContent = true;
      return;
    }

    // Email verificado pero WhatsApp no → llevar directo al paso 4
    if (state.emailVerified && !state.phoneVerified) {
      const isPersonal = state.accountType === 'personal';
      const accountLabel = isPersonal ? 'Personal' : 'Empresa';
      const accountIcon = isPersonal ? 'assets/img/users.png' : 'assets/img/wallet.png';

      const result = await Swal.fire({
        html: this.buildProgressSummary(state, accountLabel, accountIcon),
        showCancelButton: true,
        showCloseButton: false,
        confirmButtonText: 'Continuar registro',
        cancelButtonText: 'Iniciar nuevo registro',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: true,
        customClass: {
          popup: 'aguacate-swal-popup',
          confirmButton: 'aguacate-swal-confirm',
          cancelButton: 'aguacate-swal-cancel',
          htmlContainer: 'aguacate-swal-html',
          actions: 'aguacate-swal-actions',
        },
        buttonsStyling: false,
      });

      if (result.isConfirmed) {
        this.signupState.setStep(PASOS_COMUNES.OTP_WHATSAPP);
      } else {
        try { await this.cognitoService.signOut(); } catch (e) {}
        this.signupState.reset();
      }

      this.showContent = true;
      return;
    }

    // Ambas verificaciones completas → mostrar alert
    if (state.emailVerified && state.phoneVerified) {

      // Si el usuario ya confirmó continuar desde app.component, no mostrar doble alert
      const alreadyConfirmed = sessionStorage.getItem('aguacate_continue_registration');
      if (alreadyConfirmed) {
        sessionStorage.removeItem('aguacate_continue_registration');
        this.showContent = true;
        return;
      }

      const isPersonal = state.accountType === 'personal';
      const accountLabel = isPersonal ? 'Personal' : 'Empresa';
      const accountIcon = isPersonal ? 'assets/img/users.png' : 'assets/img/wallet.png';

      const result = await Swal.fire({
        html: this.buildProgressSummary(state, accountLabel, accountIcon),
        showCancelButton: true,
        showCloseButton: false,
        confirmButtonText: 'Continuar registro',
        cancelButtonText: 'Iniciar nuevo registro',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: true,
        customClass: {
          popup: 'aguacate-swal-popup',
          confirmButton: 'aguacate-swal-confirm',
          cancelButton: 'aguacate-swal-cancel',
          htmlContainer: 'aguacate-swal-html',
          actions: 'aguacate-swal-actions',
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        try {
          await this.cognitoService.signOut();
        } catch (e) {
          // Ignorar si no hay sesión activa
        }
        this.signupState.reset();
      }
    }

    this.showContent = true;
  }

  private buildProgressSummary(state: SignupState, accountLabel: string, accountIcon: string): string {
    const steps: { label: string; value: string; done: boolean }[] = [];

    // Correo
    if (state.formData?.email) {
      steps.push({
        label: 'Correo',
        value: state.formData.email,
        done: !!state.emailVerified,
      });
    }

    // WhatsApp
    if (state.formData?.phone) {
      const phone = `${state.formData.phoneCode} ${state.formData.phone}`;
      steps.push({
        label: 'WhatsApp',
        value: phone,
        done: !!state.phoneVerified,
      });
    }

    // Pasos adicionales de persona
    if (state.accountType === 'personal') {
      if (state.ocrData) {
        steps.push({ label: 'Documento', value: 'Escaneado', done: true });
      }
      if (state.faceVerified) {
        steps.push({ label: 'Verificaci\u00f3n facial', value: 'Completada', done: true });
      }
    }

    const stepsHtml = steps.map(s => `
      <div class="ag-step-row">
        <span class="ag-step-icon ${s.done ? 'done' : 'pending'}">
          ${s.done
            ? '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#0e7705"/><path d="M4.5 8L7 10.5L11.5 5.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#8a8f98"/><circle cx="8" cy="8" r="3" fill="white"/></svg>'
          }
        </span>
        <div class="ag-step-info">
          <span class="ag-step-label">${s.label}</span>
          <span class="ag-step-value">${s.value}</span>
        </div>
        <span class="ag-step-badge ${s.done ? 'done' : 'pending'}">${s.done ? 'Verificado' : 'Pendiente'}</span>
      </div>
    `).join('');

    const progress = Math.round(((state.currentStep - 1) / this.signupState.totalSteps) * 100);

    return `
      <style>
        .aguacate-swal-popup { font-family: 'Poppins', sans-serif !important; border-radius: 16px !important; padding: 2rem 1.5rem !important; max-width: 400px !important; }
        .aguacate-swal-html { margin: 0 !important; padding: 0 !important; }
        .aguacate-swal-actions { flex-direction: column !important; gap: 0.5rem !important; width: 100% !important; padding: 0 !important; margin-top: 0 !important; }
        .aguacate-swal-confirm { font-family: 'Poppins', sans-serif !important; background-color: #0e7705 !important; color: white !important; border: none !important; border-radius: 8px !important; padding: 0.75rem 1.5rem !important; font-weight: 600 !important; font-size: 0.95rem !important; width: 100% !important; cursor: pointer !important; transition: background-color 0.2s !important; }
        .aguacate-swal-confirm:hover { background-color: #154e3a !important; }
        .aguacate-swal-cancel { font-family: 'Poppins', sans-serif !important; background-color: transparent !important; color: #8a8f98 !important; border: 1px solid #e5e7eb !important; border-radius: 8px !important; padding: 0.75rem 1.5rem !important; font-weight: 500 !important; font-size: 0.95rem !important; width: 100% !important; cursor: pointer !important; transition: all 0.2s !important; }
        .aguacate-swal-cancel:hover { background-color: #f9fafb !important; color: #082c22 !important; border-color: #8a8f98 !important; }

        .ag-header { text-align: center; margin-bottom: 1.25rem; }
        .ag-header img { width: 48px; height: 48px; margin-bottom: 0.5rem; }
        .ag-header-title { font-size: 1.1rem; font-weight: 700; color: #082c22; margin: 0; }
        .ag-header-type { display: inline-block; background-color: #b2f0c0; color: #154e3a; font-size: 0.75rem; font-weight: 600; padding: 0.2rem 0.75rem; border-radius: 999px; margin-top: 0.35rem; }

        .ag-progress-section { margin-bottom: 1rem; }
        .ag-progress-bar-bg { width: 100%; height: 8px; background-color: #e5e7eb; border-radius: 999px; overflow: hidden; }
        .ag-progress-bar-fill { height: 100%; background-color: #0e7705; border-radius: 999px; transition: width 0.3s; }
        .ag-progress-text { font-size: 0.75rem; color: #8a8f98; margin-top: 0.35rem; text-align: right; }

        .ag-steps-card { background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 0.75rem; margin-bottom: 1rem; }
        .ag-step-row { display: flex; align-items: center; padding: 0.5rem 0.25rem; border-bottom: 1px solid #f3f4f6; gap: 0.5rem; }
        .ag-step-row:last-child { border-bottom: none; }
        .ag-step-icon { flex-shrink: 0; display: flex; align-items: center; }
        .ag-step-info { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .ag-step-label { font-size: 0.7rem; color: #8a8f98; text-transform: uppercase; letter-spacing: 0.5px; }
        .ag-step-value { font-size: 0.85rem; color: #082c22; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ag-step-badge { flex-shrink: 0; font-size: 0.65rem; font-weight: 600; padding: 0.15rem 0.5rem; border-radius: 999px; }
        .ag-step-badge.done { background-color: #b2f0c0; color: #154e3a; }
        .ag-step-badge.pending { background-color: #fef3c7; color: #92400e; }
      </style>

      <div class="ag-header">
        <img src="${accountIcon}" alt="${accountLabel}" />
        <p class="ag-header-title">Registro en progreso</p>
        <span class="ag-header-type">Cuenta ${accountLabel}</span>
      </div>

      <div class="ag-progress-section">
        <div class="ag-progress-bar-bg">
          <div class="ag-progress-bar-fill" style="width: ${progress}%"></div>
        </div>
        <div class="ag-progress-text">Paso ${state.currentStep} de ${this.signupState.totalSteps}</div>
      </div>

      <div class="ag-steps-card">
        ${stepsHtml}
      </div>
    `;
  }

  // Getters para el template
  get currentStep(): number {
    return this.signupState.currentStep;
  }

  get totalSteps(): number {
    return this.signupState.totalSteps;
  }

  get progressPercentage(): number {
    return this.signupState.progressPercentage;
  }

  get formEmail(): string {
    return this.state?.formData?.email || '';
  }

  get formPhone(): string {
    const data = this.state?.formData;
    return data ? `${data.phoneCode} ${data.phone}` : '';
  }

  // Error handling (recibido de sub-componentes)
  handleError(message: string): void {
    this.errorMessage = message;
    clearTimeout(this.errorTimeout);
    this.errorTimeout = setTimeout(() => {
      this.errorMessage = '';
    }, 10000);
  }

  // Success handling (recibido de sub-componentes)
  handleSuccess(message: string): void {
    this.successMessage = message;
    clearTimeout(this.successTimeout);
    this.successTimeout = setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  // Loading control
  setLoading(loading: boolean): void {
    this.loadingFull = loading;
  }

  // Navigation helpers
  async goToPrevStep(): Promise<void> {
    await this.signupState.prevStep();
  }

  async goToNextStep(): Promise<void> {
    await this.signupState.nextStep();
  }
}
